import $i18n from 'panda-i18n';
import React, { forwardRef, useState, useImperativeHandle, useMemo, } from 'react';
import { withNativeProps } from '@/utils/with-native-props';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { mergeProps } from '@/utils/with-default-props';
import { ArrowLeft } from './arrow-left';
import { ArrowLeftDouble } from './arrow-left-double';
// import { useConfig } from './config-provider';
import isoWeek from 'dayjs/plugin/isoWeek';
import localeData from 'dayjs/plugin/localeData';
import 'dayjs/locale/zh-cn';
import { useUpdateEffect } from 'ahooks';
import { usePropsValue } from '@/utils/use-props-value';
import { convertValueToRange, convertPageToDayjs, } from './convert';
import CalendarTimePicker from './time-picker';
dayjs.extend(isoWeek);
dayjs.extend(localeData);
dayjs.locale('zh-cn');
const classPrefix = 'cn-ui-m-calendar';
const renderYearAndMonth = (year, month) => $i18n.get({
    id: 'YearYearMonthMonth',
    dm: '{year}年{month}月',
    ns: 'CnCalendar',
}, { year: year, month: month });
const defaultProps = {
    weekStartsOn: 'Sunday',
    defaultValue: null,
    allowClear: true,
    prevMonthButton: React.createElement(ArrowLeft, null),
    prevYearButton: React.createElement(ArrowLeftDouble, null),
    nextMonthButton: React.createElement(ArrowLeft, null),
    nextYearButton: React.createElement(ArrowLeftDouble, null),
};
const Calendar = forwardRef((p, ref) => {
    const today = dayjs();
    const props = mergeProps(defaultProps, p);
    // const { locale } = useConfig();
    // TODO 已挪走自添加结构
    // const markItems = [...locale.Calendar.markItems];
    const markItems = dayjs.weekdaysMin();
    if (props.weekStartsOn === 'Monday') {
        const item = markItems.shift();
        if (item)
            markItems.push(item);
    }
    const [dateRange, setDateRange] = usePropsValue({
        value: props.value === undefined
            ? undefined
            : convertValueToRange(props.selectionMode, props.value),
        defaultValue: convertValueToRange(props.selectionMode, props.defaultValue),
        onChange: (v) => {
            if (props.selectionMode === 'single') {
                props.onChange?.(v ? v[0] : null);
            }
            else if (props.selectionMode === 'range') {
                props.onChange?.(v);
            }
        },
    });
    const [intermediate, setIntermediate] = useState(false);
    const [current, setCurrent] = useState(() => {
        return dayjs(dateRange ? dateRange[0] : today).date(1);
    });
    useUpdateEffect(() => {
        props.onPageChange?.(current.year(), current.month() + 1);
    }, [current]);
    useImperativeHandle(ref, () => ({
        jumpTo: (pageOrPageGenerator) => {
            let page;
            if (typeof pageOrPageGenerator === 'function') {
                page = pageOrPageGenerator({
                    year: current.year(),
                    month: current.month() + 1,
                });
            }
            else {
                page = pageOrPageGenerator;
            }
            setCurrent(convertPageToDayjs(page));
        },
        jumpToToday: () => {
            setCurrent(dayjs().date(1));
        },
    }));
    const handlePageChange = (action, num, type) => {
        const nxtCurrent = current[action](num, type);
        if (action === 'subtract' && props.minPage) {
            const minPage = convertPageToDayjs(props.minPage);
            if (nxtCurrent.isBefore(minPage, type)) {
                return;
            }
        }
        if (action === 'add' && props.maxPage) {
            const maxPage = convertPageToDayjs(props.maxPage);
            if (nxtCurrent.isAfter(maxPage, type)) {
                return;
            }
        }
        setCurrent(current[action](num, type));
    };
    const header = (React.createElement("div", { className: `${classPrefix}-header` },
        React.createElement("a", { className: `${classPrefix}-arrow-button ${classPrefix}-arrow-button-year`, onClick: () => {
                handlePageChange('subtract', 1, 'year');
            } }, props.prevYearButton),
        React.createElement("a", { className: `${classPrefix}-arrow-button ${classPrefix}-arrow-button-month`, onClick: () => {
                handlePageChange('subtract', 1, 'month');
            } }, props.prevMonthButton),
        React.createElement("div", { className: `${classPrefix}-title` }, renderYearAndMonth(current.year(), current.month() + 1)),
        React.createElement("a", { className: classNames(`${classPrefix}-arrow-button`, `${classPrefix}-arrow-button-right`, `${classPrefix}-arrow-button-right-month`), onClick: () => {
                handlePageChange('add', 1, 'month');
            } }, props.nextMonthButton),
        React.createElement("a", { className: classNames(`${classPrefix}-arrow-button`, `${classPrefix}-arrow-button-right`, `${classPrefix}-arrow-button-right-year`), onClick: () => {
                handlePageChange('add', 1, 'year');
            } }, props.nextYearButton)));
    const maxDay = useMemo(() => props.max && dayjs(props.max), [props.max]);
    const minDay = useMemo(() => props.min && dayjs(props.min), [props.min]);
    function renderCells() {
        const cells = [];
        let iterator = current.subtract(current.isoWeekday(), 'day');
        if (props.weekStartsOn === 'Monday') {
            iterator = iterator.add(1, 'day');
        }
        while (cells.length < 6 * 7) {
            const d = iterator;
            let isSelect = false;
            let isBegin = false;
            let isEnd = false;
            if (dateRange) {
                const [begin, end] = dateRange;
                isBegin = d.isSame(begin, 'day');
                isEnd = d.isSame(end, 'day');
                isSelect =
                    isBegin ||
                        isEnd ||
                        (d.isAfter(begin, 'day') && d.isBefore(end, 'day'));
            }
            const inThisMonth = d.month() === current.month();
            const disabled = props.disabledDate
                ? props.disabledDate(d.toDate(), 'date')
                : (maxDay && d.isAfter(maxDay, 'day')) ||
                    (minDay && d.isBefore(minDay, 'day'));
            cells.push(React.createElement("div", { key: d.valueOf(), className: classNames(`${classPrefix}-cell`, (disabled || !inThisMonth) && `${classPrefix}-cell-disabled`, inThisMonth && {
                    [`${classPrefix}-cell-today`]: d.isSame(today, 'day'),
                    [`${classPrefix}-cell-selected`]: isSelect,
                    [`${classPrefix}-cell-selected-begin`]: isBegin,
                    [`${classPrefix}-cell-selected-end`]: isEnd,
                }), onClick: () => {
                    if (!props.selectionMode)
                        return;
                    if (disabled)
                        return;
                    const date = d.toDate();
                    if (!inThisMonth) {
                        setCurrent(d.clone().date(1));
                    }
                    function shouldClear() {
                        if (!props.allowClear)
                            return false;
                        if (!dateRange)
                            return false;
                        const [begin, end] = dateRange;
                        return d.isSame(begin, 'date') && d.isSame(end, 'day');
                    }
                    if (props.selectionMode === 'single') {
                        if (props.allowClear && shouldClear()) {
                            setDateRange(null);
                            return;
                        }
                        setDateRange([date, date]);
                    }
                    else if (props.selectionMode === 'range') {
                        if (!dateRange) {
                            setDateRange([date, date]);
                            setIntermediate(true);
                            return;
                        }
                        if (shouldClear()) {
                            setDateRange(null);
                            setIntermediate(false);
                            return;
                        }
                        if (intermediate) {
                            const another = dateRange[0];
                            setDateRange(another > date ? [date, another] : [another, date]);
                            setIntermediate(false);
                        }
                        else {
                            setDateRange([date, date]);
                            setIntermediate(true);
                        }
                    }
                } },
                React.createElement("div", { className: `${classPrefix}-cell-top` }, d.date()),
                React.createElement("div", { className: `${classPrefix}-cell-bottom` }, props.renderLabel?.(d.toDate()))));
            iterator = iterator.add(1, 'day');
        }
        return cells;
    }
    const body = React.createElement("div", { className: `${classPrefix}-cells` }, renderCells());
    const mark = (React.createElement("div", { className: `${classPrefix}-mark` }, markItems.map((item, index) => (React.createElement("div", { key: index, className: `${classPrefix}-mark-cell` }, item)))));
    let time = null;
    if (props.showTime) {
        time = (React.createElement(CalendarTimePicker, { ...props.timePanelProps, label: $i18n.get({ id: 'Time', dm: '时间', ns: 'CnCalendar' }), prefix: classPrefix, value: dateRange ? dateRange[0] : today.toDate(), onChange: (date) => {
                props.onChange?.(date);
            } }));
    }
    return withNativeProps(props, React.createElement("div", { className: classPrefix },
        header,
        mark,
        body,
        time));
});
const CnCalendar = Calendar;
CnCalendar.displayName = 'CnCalendar';
export { CnCalendar };
